import {
  CheckCircleOutline,
  CloseFullscreen,
  InfoOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import {
  AdvancedDatePicker,
  Breadcrumb,
  Button,
  BUTTON_SIZE,
  BUTTON_VARIANT,
  Chip,
  Dropdown,
  FormControlLabel,
  Modal,
  RotationCarousel,
  Switch,
  theme as dsTheme,
  Typography,
} from '@sb/cc-design-system';
import dayjs from 'dayjs';
import {
  CreateCcDataResult,
  IAssemblyScreen,
  IComponents,
  ISection,
  RotationControlSignals,
} from 'gcf-player/shared-utils';
import { debounce, isEmpty, toString } from 'lodash';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthDataProvider } from 'src/components/authProvider';
import { EmptyProvider } from 'src/components/emptyProvider';
import { GcfPreview } from 'src/components/gcfPreview';
import { filterOutDynamicSection } from 'src/components/gcfPreview/GcfPreview.helper';
import {
  ComponentInstanceData,
  SelectedSection,
} from 'src/components/gcfPreview/GcfPreview.interface';
import LayoutPanel from 'src/components/layoutPanel';
import { LoadingProvider } from 'src/components/loadingProvider';
import { PublicationModalV2 } from 'src/components/modal/publicationModal/PublicationModalV2';
import {
  DEFAULT_SCREEN_ALL,
  IOrientation,
  ISO_DATE_FORMAT,
  US_DATE_SLASH_FORMAT,
} from 'src/constants/constants';
import { PATHS } from 'src/constants/paths';
import {
  getMaxPreviewDate,
  getUniqueSortedPositions,
} from 'src/features/locations/schedulePreviewPage/SchedulePreviewPage.helper';
import { useGetLocation } from 'src/hooks';
import { useNavigateBackHandler } from 'src/hooks/navigation';
import { useSharedUtilsFromGcfPlayer } from 'src/hooks/useSharedUtilsFromGcfPlayer';
import { IDaypart } from 'src/interfaces/Daypart';
import { PublicationTypes } from 'src/interfaces/Publication';
import {
  ISchedulePreviewContext,
  LocationChannels,
} from 'src/interfaces/SchedulePreview';
import { lang } from 'src/utils';

import {
  getRotationCarouselDisabledState,
  isPlaybackDisabled,
} from './MenuBoardModificationPage.helper';
import { useMenuBoardModificationPage } from './MenuBoardModificationPage.hook';
import {
  ButtonsWrapper,
  ContentHeader,
  GCFPreviewBox,
  MenuBoardModificationPageWrapper,
  SectionClickInfo,
  StyledToolbarGroup,
  ValidationChipList,
  ValidationHeaderInfo,
  ViewAllButton,
} from './MenuBoardModificationPage.styled';
import {
  getNextRotationPosition,
  pauseInPlace,
} from './MenuBoardPlayback.helper';
import { HelpButton } from './modules/HelpButton/HelpButton';
import { MenuBoardPlaybackController } from './modules/menuBoardPlayback';
import { OverrideLeftSideBar } from './modules/overrideLeftSideBar';
import { OverrideRightSideBar } from './modules/overrideRightSideBar';
import { PlaybackControls } from './modules/playbackControls';
import { ProductOverrideRightSideBar } from './modules/productOverrideRightSideBar';

const ROTATION_JUMP_DEBOUNCE_MS = 180;

export const MenuBoardModificationPage = () => {
  const { userRole } = useContext(AuthDataProvider);

  const navigate = useNavigate();
  const routerLocation = useLocation();
  const {
    showAllowOverrideSections,
    previewContext,
    isSchedulePreviewContextLoading,
    isSchedulePreviewContextFetching,
    selectedDaypartData,
    selectedDate,
    previewStartDate,
    selectedPosition,
    selectedScreen,
    daypartOptions,
    selectedDaypart,
    isLoadingDayparts,
    locationCode,
    locationGroupId,
    channelName,
    currentSelectedSection,
    selectedSectionDetailInfo,
    selectedScheduleItem,
    overrideChangeSetItems,
    hasReachedLocalOverrideLimit,
    isAddOverrideDisabled,
    overrideCardIdToScroll,
    overrideValidationChips,
    overrideIdToDelete,
    matchedComponents,
    isMatchedComponentsLoading,
    isMatchedComponentsFetching,
    hasNextMatchedComponentsPage,
    isFetchingNextMatchedComponentsPage,
    bulkUpdateScheduleOverridesConfirmationModal,
    publicationModal,
    bulkUpdateScheduleOverrides,
    isSaveButtonDisabled,
    selectProductKey,
    isProductLineupOverride,
    isProductLoading,
    localAttributes,
    setLocalAttributes,
    defaultSize,
    defaultFlavor,
    showImage,
    defaultImage,
    productVariationsAssets,
    lineupFallbackProducts,
    fetchNextMatchedComponentsPage,
    handleSearchMatchedComponents,
    onChangeParams,
    handleChangeSelectedDate,
    handleChangeSelectedDaypart,
    handleChangeSelectedScreen,
    handleChangeToggleValue,
    handleChangeOverrideStartDate,
    handleChangeOverrideEndDate,
    handleDeleteOverride,
    handleDeleteExistingOverride,
    handleCloseOverrideDeleteModal,
    handleConfirmDeleteOverride,
    handleUnselectSection,
    handleClickSection,
    handleOverrideValidationChipClick,
    handleOverrideCardScrollComplete,
    handleAddOverride,
    handleSelectPromo,
    handleSave,
    setCurrentSelectedSection,
    onSelectAccordionProduct,
    onReorderAccordionProduct,
    onAddAccordionProducts,
    onReplaceAccordionProduct,
    handleProductAttributeChange,
    onRemoveAccordionProduct,
  } = useMenuBoardModificationPage();

  const {
    utils,
    loading: isSharedUtilsLoading,
    error,
  } = useSharedUtilsFromGcfPlayer();
  const { data: location } = useGetLocation(locationCode, userRole);

  const isContentLoading =
    isSharedUtilsLoading || isSchedulePreviewContextLoading;
  const navigateBackLink =
    (routerLocation.state as { navigateBackLink?: string } | null)
      ?.navigateBackLink ?? PATHS.LOCATION_DETAILS(locationCode);
  const { handleNavigateBackWithoutBlocker } = useNavigateBackHandler(
    navigateBackLink,
    !isContentLoading && !!locationGroupId && !!locationCode,
  );

  const channelLabel = decodeURIComponent(channelName || '');
  // Computed in a useEffect (not useMemo) so this transform runs after
  // first paint instead of blocking the click-to-paint commit on warm-cache reloads.
  const [previewData, setPreviewData] = useState<
    CreateCcDataResult | undefined
  >(undefined);

  useEffect(() => {
    if (
      isSchedulePreviewContextLoading ||
      isEmpty(previewContext) ||
      !selectedDaypartData ||
      !utils
    ) {
      setPreviewData(undefined);

      return;
    }

    const {
      contents,
      location,
      products,
      config,
      assets,
      components,
      overrides,
      restaurantBridgeData,
    } = previewContext as ISchedulePreviewContext;

    setPreviewData(
      utils.createCcData(
        contents,
        location,
        undefined,
        products,
        config,
        assets,
        components,
        overrides ?? {},
        restaurantBridgeData,
        undefined,
        {
          daypartName: selectedDaypartData.name,
          datetime: selectedDate.format(ISO_DATE_FORMAT),
        },
      ),
    );
  }, [
    isSchedulePreviewContextLoading,
    previewContext,
    selectedDaypartData,
    selectedDate,
    utils,
  ]);
  const lineupProducts = useMemo(
    () =>
      isEmpty(lineupFallbackProducts)
        ? previewData?.ccData?.products
        : {
            ...lineupFallbackProducts,
            ...(previewData?.ccData?.products ?? {}),
          },
    [lineupFallbackProducts, previewData?.ccData?.products],
  );

  const uniqueSortedPositions = getUniqueSortedPositions(
    selectedDate,
    previewData?.ccData?.location?.channels as LocationChannels,
    selectedDaypartData?.name,
  );

  const isSelectedPositionInvalid = !uniqueSortedPositions.includes(
    Number(selectedPosition),
  );

  useEffect(() => {
    if (!previewData || isSchedulePreviewContextFetching) {
      return;
    }

    if (isSelectedPositionInvalid) {
      onChangeParams('position', '1');
    }
  }, [
    isSelectedPositionInvalid,
    previewData,
    isSchedulePreviewContextFetching,
    onChangeParams,
  ]);

  const pendingJumpRef = useRef<{
    position: number;
    signals: RotationControlSignals;
  } | null>(null);

  const handleActivePositionChange = useCallback(
    (position: number) => {
      onChangeParams('position', toString(position));
    },
    [onChangeParams],
  );

  const debouncedJumpRef = useRef(
    debounce(() => {
      const pending = pendingJumpRef.current;
      if (!pending) return;
      pendingJumpRef.current = null;
      pauseInPlace(pending.signals);
      pending.signals.jump(pending.position);
    }, ROTATION_JUMP_DEBOUNCE_MS),
  );

  useEffect(() => {
    const debouncedJump = debouncedJumpRef.current;

    return () => debouncedJump.cancel();
  }, []);

  const handleRotationArrowClick = useCallback(
    (signals: RotationControlSignals, direction: 'left' | 'right') => {
      const base = pendingJumpRef.current?.position ?? signals.activePosition;
      const nextPosition = getNextRotationPosition(
        uniqueSortedPositions,
        base,
        direction,
      );

      if (nextPosition === base) return;

      pendingJumpRef.current = { position: nextPosition, signals };
      debouncedJumpRef.current();

      if (currentSelectedSection) {
        handleUnselectSection();
      }
    },
    [currentSelectedSection, handleUnselectSection, uniqueSortedPositions],
  );

  const handleGcfPreviewClick = (
    signals: RotationControlSignals,
    data: ComponentInstanceData,
    selectedSection: SelectedSection | undefined,
  ) => {
    debouncedJumpRef.current.cancel();
    pendingJumpRef.current = null;
    pauseInPlace(signals);

    handleClickSection(
      data,
      selectedSection,
      previewData?.ccData.components as IComponents,
      previewData?.ccData.overrides?.scheduleItems ?? {},
    );
  };

  const currentSelectedSectionPosition = useMemo(
    () => ({
      screenSequence: currentSelectedSection?.screenSequence,
      left: currentSelectedSection?.left,
      top: currentSelectedSection?.top,
    }),
    [currentSelectedSection],
  );

  useEffect(() => {
    if (!currentSelectedSectionPosition) {
      return;
    }

    const screens = previewData?.activeAssembly?.screens;

    if (!screens) return;

    const sectionAtSelectedPosition = filterOutDynamicSection(
      (screens as Record<string, IAssemblyScreen>)[
        String(currentSelectedSectionPosition.screenSequence)
      ]?.sections,
    )?.find((section: ISection) => {
      const normalisedSelectedPosition = Number(selectedPosition);

      return (
        section.config?.position === normalisedSelectedPosition &&
        section.left === currentSelectedSectionPosition.left &&
        section.top === currentSelectedSectionPosition.top
      );
    });

    if (sectionAtSelectedPosition) {
      const nextSelectedSection = {
        id: sectionAtSelectedPosition.id,
        position: sectionAtSelectedPosition.config?.position as number,
        screenSequence: currentSelectedSectionPosition.screenSequence as number,
        left: currentSelectedSectionPosition.left as number,
        top: currentSelectedSectionPosition.top as number,
      };

      setCurrentSelectedSection((prevSelectedSection) => {
        if (
          prevSelectedSection?.id === nextSelectedSection.id &&
          String(prevSelectedSection?.position) ===
            String(nextSelectedSection.position) &&
          prevSelectedSection?.screenSequence ===
            nextSelectedSection.screenSequence &&
          prevSelectedSection?.left === nextSelectedSection.left &&
          prevSelectedSection?.top === nextSelectedSection.top
        ) {
          return prevSelectedSection;
        }

        return nextSelectedSection;
      });
    }
  }, [
    previewData,
    selectedPosition,
    setCurrentSelectedSection,
    currentSelectedSectionPosition,
  ]);

  return (
    <MenuBoardModificationPageWrapper>
      <LayoutPanel data-testid="layout-panel">
        <LayoutPanel.Header data-testid="layout-header">
          <Breadcrumb
            showHome={false}
            items={[
              {
                label: lang.locations,
                onClick: () => navigate(PATHS.OO_LOCATION_PAGE),
              },
              {
                label: location?.name || locationCode,
                onClick: () => navigate(PATHS.LOCATION_DETAILS(locationCode)),
              },
              {
                label: channelLabel,
                onClick: () => navigate(PATHS.LOCATION_DETAILS(locationCode)),
              },
              { label: lang.editBoard },
            ]}
          />

          <ButtonsWrapper>
            <Button
              variant={BUTTON_VARIANT.outlinedSecondary1}
              size={BUTTON_SIZE.sm}
              onClick={() => {
                navigate(PATHS.LOCATION_DETAILS(locationCode));
              }}>
              {lang.cancel}
            </Button>
            <Button
              size={BUTTON_SIZE.sm}
              disabled={isSaveButtonDisabled}
              onClick={handleSave}>
              {lang.save}
            </Button>
          </ButtonsWrapper>
        </LayoutPanel.Header>
        <LayoutPanel.Header isSecondHeader>
          {overrideValidationChips.length ? (
            <ValidationChipList>
              {overrideValidationChips.map((chip) => (
                <Chip
                  key={chip.id}
                  label={chip.label}
                  chipType={chip.chipType}
                  maxWidth={134}
                  handleChipClick={() =>
                    handleOverrideValidationChipClick(chip.id)
                  }
                  endIcon={
                    chip.chipType === 'success' ? (
                      <CheckCircleOutline fontSize="small" />
                    ) : (
                      <WarningAmberOutlined fontSize="small" />
                    )
                  }
                />
              ))}
            </ValidationChipList>
          ) : (
            <ValidationHeaderInfo>
              <Typography variant="caption">
                {lang.scheduleComponent.preview.makeEditToViewOverrides}
              </Typography>
              <InfoOutlined />
            </ValidationHeaderInfo>
          )}
        </LayoutPanel.Header>
        <LayoutPanel.ContentContainer data-testid="layout-container">
          <LayoutPanel.LeftSideBar
            data-testid="layout-left-sidebar"
            padding="16px">
            <OverrideLeftSideBar
              selectedSectionDetailInfo={selectedSectionDetailInfo}
              selectedScheduleItem={selectedScheduleItem}
              overrideChangeSetItems={overrideChangeSetItems}
              hasReachedLocalOverrideLimit={hasReachedLocalOverrideLimit}
              isAddOverrideDisabled={isAddOverrideDisabled}
              overrideCardIdToScroll={overrideCardIdToScroll}
              previewStartDate={previewStartDate}
              products={lineupProducts}
              selectProductKey={selectProductKey}
              onAddOverride={handleAddOverride}
              onChangeOverrideStartDate={handleChangeOverrideStartDate}
              onChangeOverrideEndDate={handleChangeOverrideEndDate}
              onDeleteOverride={handleDeleteOverride}
              onDeleteExistingOverride={handleDeleteExistingOverride}
              onOverrideCardScrollComplete={handleOverrideCardScrollComplete}
              onSelectAccordionProduct={onSelectAccordionProduct}
              onReorderAccordionProduct={onReorderAccordionProduct}
              onAddAccordionProducts={onAddAccordionProducts}
              onReplaceAccordionProduct={onReplaceAccordionProduct}
              onRemoveAccordionProduct={onRemoveAccordionProduct}
            />
          </LayoutPanel.LeftSideBar>
          <LayoutPanel.Content
            data-testid="layout-content"
            padding="8px 20px"
            display={'flex'}
            flexDirection={'column'}>
            <LoadingProvider
              isLoading={
                isSharedUtilsLoading || isSchedulePreviewContextLoading
              }>
              {!error ? (
                <MenuBoardPlaybackController
                  previewData={previewData}
                  initialPosition={Number(selectedPosition)}
                  onActivePositionChange={handleActivePositionChange}>
                  {(signals: RotationControlSignals) => (
                    <>
                      <ContentHeader>
                        <FormControlLabel
                          label={lang.scheduleComponent.override.toggleDesc}
                          sx={{ justifySelf: 'start', gap: '8px' }}
                          control={
                            <Switch
                              checked={showAllowOverrideSections}
                              onChange={(e) =>
                                handleChangeToggleValue(e.target.checked)
                              }
                            />
                          }
                        />
                        {toString(selectedScreen) !== DEFAULT_SCREEN_ALL && (
                          <ViewAllButton
                            variant="contained-secondary"
                            size="small"
                            onClick={() =>
                              handleChangeSelectedScreen(DEFAULT_SCREEN_ALL)
                            }
                            endIcon={<CloseFullscreen fontSize="small" />}>
                            {lang.scheduleComponent.viewAll}
                          </ViewAllButton>
                        )}
                        {currentSelectedSection && (
                          <SectionClickInfo variant="body2">
                            {lang.scheduleComponent.preview.sectionClickInfo}
                          </SectionClickInfo>
                        )}
                      </ContentHeader>
                      <GCFPreviewBox>
                        <EmptyProvider
                          message={
                            lang.scheduleComponent.preview.noPreviewAvailable
                          }
                          dataToCheck={previewData?.activeAssembly}>
                          <GcfPreview
                            previewData={previewData}
                            activeScreen={selectedScreen}
                            highlightAll={!showAllowOverrideSections}
                            showSectionPill={false}
                            selectedSection={currentSelectedSection}
                            signals={signals}
                            minScreenSize={{
                              [IOrientation.VERTICAL]: {
                                width: 210,
                                height: 373,
                              },
                              [IOrientation.HORIZONTAL]: {
                                width: 373,
                                height: 210,
                              },
                            }}
                            onScreenLabelClick={(screenSequence) => {
                              const isExpandedScreenSelected =
                                String(selectedScreen) ===
                                String(screenSequence);

                              handleChangeSelectedScreen(
                                isExpandedScreenSelected
                                  ? DEFAULT_SCREEN_ALL
                                  : String(screenSequence),
                              );
                            }}
                            onUnselectSection={handleUnselectSection}
                            onClick={(data, selectedSection) =>
                              handleGcfPreviewClick(
                                signals,
                                data,
                                selectedSection,
                              )
                            }
                          />
                        </EmptyProvider>
                      </GCFPreviewBox>
                      <StyledToolbarGroup
                        slotInternalSpacingX={12}
                        backgroundColor={dsTheme.palette.primary.contrastText}>
                        <AdvancedDatePicker
                          value={selectedDate}
                          minDate={dayjs()}
                          maxDate={getMaxPreviewDate()}
                          format={US_DATE_SLASH_FORMAT}
                          disabled={
                            !previewData?.activeAssembly ||
                            signals.status === 'PLAYING'
                          }
                          showBorder={false}
                          dateColor={dsTheme.palette.secondary.contrastText}
                          arrowColor={dsTheme.palette.secondary.contrastText}
                          onChange={(date) => handleChangeSelectedDate(date)}
                        />

                        {!isLoadingDayparts && (
                          <Dropdown
                            data-testid="daypart-dropdown"
                            options={(daypartOptions as IDaypart[])?.map(
                              (daypart) => ({
                                label: daypart.name,
                                value: daypart.id,
                              }),
                            )}
                            value={daypartOptions ? selectedDaypart : ''}
                            disabled={signals.status === 'PLAYING'}
                            onChange={(value) => {
                              handleChangeSelectedDaypart(toString(value));
                            }}
                          />
                        )}

                        {uniqueSortedPositions && (
                          <RotationCarousel
                            label={`Rotation ${signals.activePosition}`}
                            disabled={getRotationCarouselDisabledState(
                              previewData?.activeAssembly,
                              signals.activePosition,
                              uniqueSortedPositions,
                              signals.status,
                            )}
                            onArrowClick={(direction) =>
                              handleRotationArrowClick(signals, direction)
                            }
                          />
                        )}
                        <PlaybackControls
                          status={signals.status}
                          play={signals.play}
                          pause={signals.pause}
                          resume={signals.resume}
                          disabled={isPlaybackDisabled(
                            previewData?.activeAssembly,
                            signals.totalPositions,
                          )}
                        />
                        <HelpButton />
                      </StyledToolbarGroup>
                    </>
                  )}
                </MenuBoardPlaybackController>
              ) : (
                <></>
              )}
            </LoadingProvider>
          </LayoutPanel.Content>
          <LayoutPanel.RightSideBar
            data-testid="layout-right-sidebar"
            padding="16px">
            {isProductLineupOverride ? (
              <ProductOverrideRightSideBar
                localAttributes={localAttributes}
                defaultSize={defaultSize}
                defaultFlavor={defaultFlavor}
                showImage={showImage}
                defaultImage={defaultImage}
                isLoading={!!selectProductKey && isProductLoading}
                productVariationsAssets={productVariationsAssets}
                onAttributeChange={(changes) => {
                  if (changes.attributeSizes) {
                    setLocalAttributes(changes.attributeSizes);
                  }
                  handleProductAttributeChange(changes, selectProductKey);
                }}
              />
            ) : (
              <OverrideRightSideBar
                selectedSectionDetailInfo={selectedSectionDetailInfo}
                hasClickedAddOverride={overrideChangeSetItems.length > 0}
                matchedComponents={matchedComponents}
                isMatchedComponentsLoading={isMatchedComponentsLoading}
                showMatchedComponentsLoader={isMatchedComponentsFetching}
                hasNextMatchedComponentsPage={!!hasNextMatchedComponentsPage}
                fetchNextMatchedComponentsPage={fetchNextMatchedComponentsPage}
                isFetchingNextMatchedComponentsPage={
                  isFetchingNextMatchedComponentsPage
                }
                onSearchKeywordChange={handleSearchMatchedComponents}
                onSelectPromo={handleSelectPromo}
              />
            )}
          </LayoutPanel.RightSideBar>
        </LayoutPanel.ContentContainer>
      </LayoutPanel>

      {/* bulk update schedule overrides confirmation modal */}
      {bulkUpdateScheduleOverridesConfirmationModal.open && (
        <Modal
          open={bulkUpdateScheduleOverridesConfirmationModal.open}
          onClose={bulkUpdateScheduleOverridesConfirmationModal.onCloseModal}
          onCancelAction={
            bulkUpdateScheduleOverridesConfirmationModal.onCloseModal
          }
          onConfirmAction={
            bulkUpdateScheduleOverridesConfirmationModal.customFn
          }
          title={`${lang.saveChanges}?`}
          subheader={lang.saveChangesTips}
          cancelButtonText={lang.cancel}
          confirmButtonText={lang.save}
          confirmButtonProps={{
            loading: bulkUpdateScheduleOverrides.isLoading,
          }}>
          <Typography variant="body2">
            {
              lang.scheduleComponent.override
                .bulkUpdateScheduleOverridesConfirmationModalTips
            }
          </Typography>
        </Modal>
      )}

      {/* publication modal */}
      {publicationModal.open && (
        <PublicationModalV2
          title={`${lang.publicationModal.title}.`}
          open={publicationModal.open}
          publicationType={PublicationTypes.OVERRIDES}
          locationCodes={[locationCode]}
          modalSize="md"
          onClose={() => {
            publicationModal.onCloseModal();
            handleNavigateBackWithoutBlocker();
          }}
          cancelText={lang.cancel}
        />
      )}
      <Modal
        open={!!overrideIdToDelete}
        title={lang.deleteNewPromoOverrideModal.title}
        content={
          <>
            <Typography variant="body2" sx={{ mb: 3 }}>
              {lang.deleteNewPromoOverrideModal.subTitle}
            </Typography>
          </>
        }
        onClose={handleCloseOverrideDeleteModal}
        onCancelAction={handleCloseOverrideDeleteModal}
        onConfirmAction={handleConfirmDeleteOverride}
        cancelButtonText={lang.cancel}
        confirmButtonText={lang.delete}
        sizeVariant="medium"
      />
    </MenuBoardModificationPageWrapper>
  );
};
